import ImageIcon from "@rsuite/icons/Image";
import PlusIcon from "@rsuite/icons/Plus";
import { useContext, useEffect, useRef, useState, type FC } from "react";
import Barcode from "react-barcode";
import { BsCheck2Circle, BsTrash3 } from "react-icons/bs";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Carousel, IconButton, Tag, Toggle } from "rsuite";
import PriceForm from "../components/features/inventory/PriceForm";
import ProductForm from "../components/features/inventory/ProductForm";
import VariantForm from "../components/features/inventory/VariantForm";
import MainLayout from "../components/MainLayout";
import { LoadingContext } from "../contexts/LoadingContext";
import { Product, Variant } from "../models/inventory/product";
import { ProductPrice } from "../models/inventory/product_price";
import { uploadFile } from "../services/FileApi";
import {
  addDiscount,
  addPriceProduct,
  createProductVariant,
  deleteDiscount,
  deleteProductImage,
  deleteProductPrice,
  deleteProductVariant,
  getDiscounts,
  getProduct,
  getProductVariants,
  updateDiscount,
  updateProduct,
  updateProductVariant,
} from "../services/inventory/ProductApi";
import { invertColor, money, nl2br } from "../utils/helper";
import { errorToast, successToast } from "../utils/helper-ui";
import DiscountForm from "../components/features/inventory/DiscountForm";
import { Discount } from "../models/inventory/discount";
import Moment from "react-moment";
import { getStockMovements } from "../services/inventory/StockMovementApi";
import { StockMovement } from "../models/inventory/stock_movement";

import EditIcon from "@rsuite/icons/Edit";
import CheckIcon from "@rsuite/icons/Check";
import TrashIcon from "@rsuite/icons/Trash";
import { MdOutlineImageNotSupported } from "react-icons/md";

interface ProductDetailPageProps {}

const ProductDetailPage: FC<ProductDetailPageProps> = ({}) => {
  const { productId } = useParams();
  const { isLoading, setIsLoading } = useContext(LoadingContext);
  const [mounted, setMounted] = useState(false);
  const [product, setProduct] = useState<Product>();
  const fileRef = useRef<HTMLInputElement>(null);
  const [modalEdit, setModalEdit] = useState(false);
  const [variants, setVariants] = useState<Variant[]>([]);
  const [modalAddVariant, setModalAddVariant] = useState(false);
  const [modalAddDiscount, setModalAddDiscount] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState<Discount | null>(
    null
  );
  const [stockMovements, setStockMovements] = useState<StockMovement[]>([]);
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [selectedVariant, setSelectedVariant] = useState<Variant>();
  const nav = useNavigate();
  const [modalAddPrice, setModalAddPrice] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    if (!productId) return;
    getDetail();
  }, [productId, mounted]);

  const getDetail = async () => {
    try {
      setIsLoading(true);
      let resp = await getProduct(productId!);
      let respJson = await resp.json();

      setProduct(respJson.data);
      getStockMovements({ page: 1, size: 10, product_id: productId! })
        .then((v) => v.json())
        .then((v) => setStockMovements(v.data.items));
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const saveProduct = async (editedProduct: Product) => {
    try {
      setIsLoading(true);
      await updateProduct(productId!, editedProduct);
      setModalEdit(false);
      getDetail();
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const removeImage = async (id: string) => {
    try {
      const confirmRemoval = window.confirm(
        "Are you sure you want to remove this image?"
      );
      if (!confirmRemoval) return;
      setIsLoading(true);
      await deleteProductImage(productId!, id);
      getDetail();
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const addPrice = async (data: ProductPrice) => {
    try {
      setIsLoading(true);
      let resp = await addPriceProduct(productId!, data);
      await resp.json();
      setModalAddPrice(false);
      getDetail();
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (product) {
      getProductVariants(product.id!)
        .then((v) => v.json())
        .then((response) => setVariants(response.data));
      getDiscounts(product.id!)
        .then((v) => v.json())
        .then((response) => setDiscounts(response.data));
    }
  }, [product]);

  const removeVariant = async (id: string) => {
    try {
      const confirmRemoval = window.confirm(
        "Are you sure you want to remove this variant?"
      );
      if (!confirmRemoval) return;
      setIsLoading(true);
      await deleteProductVariant(productId!, id);
      getProductVariants(product!.id!)
        .then((v) => v.json())
        .then((response) => setVariants(response.data));
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };
  const renderInputFile = () => (
    <input
      type="file"
      accept="image/*"
      ref={fileRef}
      className="hidden"
      onChange={async (val) => {
        if (val.target.files?.length == 0) return;
        try {
          setIsLoading(true);
          let resp = await uploadFile(
            val.target.files![0],
            {
              ref_id: productId,
              ref_type: "product",
            },
            (progress) => {
              console.log(progress);
            }
          );
          getDetail();
        } catch (error) {
          errorToast(`${error}`);
        } finally {
          setIsLoading(false);
        }
      }}
    />
  );
  const renderImages = () => (
    <div className="w-full">
      <Carousel
        className="custom-slider"
        shape="bar"
        placement="right"
        color="orange"
      >
        {(product?.product_images ?? []).map((img, index) => (
          <div className="relative" key={index}>
            <img
              key={index}
              className="object-cover"
              src={img.url}
              height="250"
            />
            <div className="absolute top-0 right-0 cursor-pointer p-2 aspect-square rounded-full bg-black bg-opacity-20">
              <BsTrash3
                className=""
                onClick={() => removeImage(img.id)}
                size={16}
                color="white"
              />
            </div>
          </div>
        ))}
      </Carousel>
      <IconButton
        appearance="primary"
        color="orange"
        size="sm"
        className="mt-4"
        onClick={() => {
          fileRef.current?.click();
        }}
        icon={<ImageIcon />}
      >
        Add Image
      </IconButton>
    </div>
  );
  const removePrice = async (id: string) => {
    try {
      const confirmRemoval = window.confirm(
        "Are you sure you want to remove this price?"
      );
      if (!confirmRemoval) return;

      setIsLoading(true);
      await deleteProductPrice(productId!, id);
      getDetail();
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const addVariant = async (data: Variant) => {
    try {
      setIsLoading(true);
      if (selectedVariant) {
        await updateProductVariant(product!.id!, selectedVariant.id!, data);
      } else {
        await createProductVariant(product!.id!, data);
      }
      getProductVariants(product!.id!)
        .then((v) => v.json())
        .then((response) => {
          setVariants(response.data);
          setModalAddVariant(false);
          setSelectedVariant(undefined);
        });
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const createNewDiscount = async (data: Discount) => {
    try {
      setIsLoading(true);
      if (selectedDiscount) {
        await updateDiscount(selectedDiscount.id!, data);
      } else {
        await addDiscount(productId!, data);
      }
      setModalAddDiscount(false);
      getDiscounts(product!.id!)
        .then((v) => v.json())
        .then((response) => setDiscounts(response.data));
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <MainLayout>
      <div className="container">
        <div className="grid grid-cols-12 gap-4  h-full">
          <div className="col-span-4">
            <div className="w-full min-h-[300px] flex flex-col items-center justify-between">
              {(product?.product_images ?? []).length == 0 ? (
                <div className="bg-gray-50 w-full flex flex-col items-center justify-center h-300 py-10 hover:bg-gray-100">
                  <MdOutlineImageNotSupported
                    size={100}
                    color="gray"
                    className="cursor-pointer"
                    onClick={() => {
                      fileRef.current?.click();
                    }}
                  />
                </div>
              ) : (
                renderImages()
              )}
            </div>
          </div>
          <div className="col-span-8">
            <div className="flex flex-row justify-between items-start w-full">
              <div className="w-full">
                {product?.category && (
                  <Tag color="green">{product?.category?.name}</Tag>
                )}
                <div className="flex justify-between w-full">
                  <h1 className="text-2xl font-bold">{product?.name}</h1>
                </div>
              </div>
              <div className="gap-2 flex flex-row">
                {product?.status == "PENDING" && (
                  <IconButton
                    appearance="primary"
                    color="red"
                    size="xs"
                    icon={<TrashIcon />}
                    onClick={async () => {
                      try {
                        if (
                          window.confirm(
                            "Are you sure you want to reject this product?"
                          )
                        ) {
                          setIsLoading(true);
                          product.status = "REJECTED";
                          await updateProduct(productId!, product);
                          getDetail();
                        }
                      } catch (error) {
                        errorToast(`${error}`);
                      } finally {
                        setIsLoading(false);
                      }

                      // Add your edit functionality here
                    }}
                  >
                    REJECTED
                  </IconButton>
                )}
                {product?.status == "PENDING" && (
                  <IconButton
                    appearance="primary"
                    color="green"
                    size="xs"
                    icon={<CheckIcon />}
                    onClick={async () => {
                      try {
                        if (
                          window.confirm(
                            "Are you sure you want to approve this product?"
                          )
                        ) {
                          setIsLoading(true);
                          product.status = "ACTIVE";
                          await updateProduct(productId!, product);
                          getDetail();
                        }
                      } catch (error) {
                        errorToast(`${error}`);
                      } finally {
                        setIsLoading(false);
                      }

                      // Add your edit functionality here
                    }}
                  >
                    APPROVE
                  </IconButton>
                )}
                <div className="flex flex-col space-y-4">
                  <IconButton
                    appearance="primary"
                    color="orange"
                    size="xs"
                    icon={<EditIcon />}
                    onClick={() => {
                      setModalEdit(true);
                      // Add your edit functionality here
                    }}
                  >
                    Edit
                  </IconButton>
                  <Toggle
                    checkedChildren="Active"
                    unCheckedChildren="Inactive"
                    checked={product?.status == "ACTIVE"}
                    onChange={async () => {
                      setProduct({
                        ...product!,
                        status: product?.status == "ACTIVE" ? "INACTIVE" : "ACTIVE",
                      })
                      updateProduct(productId!, {
                        ...product,
                        status: product?.status == "ACTIVE" ? "INACTIVE" : "ACTIVE",
                      });
                      
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="mb-4">
              <small className="font-bold">SKU:</small>
              <p className="text-lg"> {product?.sku}</p>
            </div>
            <div className="mb-4">
              <small className="font-bold">Barcode:</small>
              {product?.barcode && (
                <Barcode value={product?.barcode} height={30} />
              )}
            </div>
            {/* {product?.master_product && (
              <div className="mb-4">
                <small className="font-bold">Master Product:</small>
                <p
                  className="text-xs hover:font-semibold cursor-pointer"
                  onClick={() =>
                    nav(`/master-product/${product?.master_product?.id}`)
                  }
                >
                  {" "}
                  {product?.master_product?.name} -{" "}
                  {product?.master_product?.sku}
                </p>
              </div>
            )} */}
            {product?.company && (
              <div className="mb-4">
                <small className="font-bold">Company:</small>
                <p className="text-lg">{product?.company?.name}</p>
              </div>
            )}
            <div className="mb-4">
              <small className="font-bold">Brand:</small>
              {product?.brand && (
                <p className="text-lg">{product?.brand?.name}</p>
              )}
            </div>
            <div className="mb-4">
              <small className="font-bold">Category:</small>
              {product?.category && (
                <p className="text-lg">{product?.category?.name}</p>
              )}
            </div>
            <div className="mb-4">
              <small className="font-bold">Dimension (L x W x H):</small>
              <p className="text-sm">
                {product?.length} x {product?.width} x {product?.height} cm
              </p>
            </div>
            <div className="mb-4">
              <small className="font-bold">Weight:</small>
              <p className="text-sm">{product?.weight} gr</p>
            </div>
            {(product?.tags ?? []).length > 0 && (
              <div className="mb-4">
                <small className="font-bold">Tags:</small>
                <div>
                  {(product?.tags ?? []).map((tag, index) => (
                    <div
                      key={index}
                      className={`inline-block bg-gray-200 rounded-full px-3 py-0.5 text-xs font-semibold text-gray-700 mr-2 `}
                      style={{
                        backgroundColor: tag.color,
                        color: invertColor(tag.color),
                      }}
                    >
                      {tag.name}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-4">
              <small className="font-bold">Total Stock:</small>
              <p className="text-lg"> {money(product?.total_stock)}</p>
            </div>
            <div className="mb-4">
              <small className="font-bold">Price:</small>
              <p className="text-lg ">
                {" "}
                {money(product?.price)}{" "}
                {product?.original_price != product?.price && (
                  <span className="line-through text-sm text-gray-400">
                    {money(product?.original_price)}
                  </span>
                )}{" "}
              </p>
            </div>
            {/* <div className="mb-4">
              <div className="flex flex-row justify-between items-center">
                <small className="font-bold">Prices:</small>
                <IconButton
                  appearance="ghost"
                  color="green"
                  size="xs"
                  className="ml-2"
                  onClick={() => {
                    setModalAddPrice(true);
                  }}
                  icon={<PlusIcon />}
                >
                  Add Price
                </IconButton>
              </div>

              {(product?.prices ?? []).length > 0 ? (
                <table className="w-full border border-gray-300 mt-4">
                  <thead>
                    <tr>
                      <th className="px-2 py-1 text-xs border text-left border-gray-300">
                        Category
                      </th>
                      <th className="px-2 py-1 text-xs border border-gray-300">
                        Amount
                      </th>
                      <th className="px-2 py-1 text-xs border border-gray-300">
                        Effective Date
                      </th>
                      <th className="px-2 py-1 text-xs border border-gray-300">
                        Min Quantity
                      </th>
                      <th className="px-2 py-1 text-xs border border-gray-300">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {product?.prices.map((price) => (
                      <tr key={price.id}>
                        <td className="px-2 py-1 text-xs border border-gray-300">
                          {price.price_category?.name}
                        </td>
                        <td className="px-2 py-1 text-xs border text-center border-gray-300">
                          {money(price.amount)}
                        </td>
                        <td className="px-2 py-1 text-xs border border-gray-300 text-center">
                          <Moment format="DD/MM/YYYY">
                            {price.effective_date}
                          </Moment>
                        </td>
                        <td className="px-2 py-1 text-xs border border-gray-300 text-center">
                          {price.min_quantity}
                        </td>
                        <td className="px-2 py-1 text-xs border border-gray-300 text-center">
                          <div className="flex justify-center">
                            <BsTrash3
                              className="text-red-500 hover:text-red-800 cursor-pointer"
                              size={12}
                              onClick={() => removePrice(price.id)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-gray-400">No Price Available</p>
              )}
            </div> */}
            <div className="mb-4">
              <div className="flex flex-row justify-between items-center">
                <small className="font-bold">Variants:</small>
                <IconButton
                  appearance="ghost"
                  color="green"
                  size="xs"
                  className="ml-2"
                  onClick={() => {
                    setModalAddVariant(true);
                  }}
                  icon={<PlusIcon />}
                >
                  Add Variant
                </IconButton>
              </div>

              {(variants ?? []).length > 0 ? (
                <table className="w-full border border-gray-300 mt-4">
                  <thead>
                    <tr>
                      <th className="px-2 py-1 text-xs border text-left border-gray-300">
                        Display Name
                      </th>
                      <th className="px-2 py-1 text-xs border border-gray-300">
                        Price
                      </th>
                      <th className="px-2 py-1 text-xs border border-gray-300">
                        Attribute
                      </th>
                      <th className="px-2 py-1 text-xs border border-gray-300">
                        Dimension (L x W x H)
                      </th>
                      <th className="px-2 py-1 text-xs border border-gray-300">
                        Weight
                      </th>
                      <th className="px-2 py-1 text-xs border border-gray-300">
                        SKU
                      </th>
                      <th className="px-2 py-1 text-xs border border-gray-300">
                        Stock
                      </th>
                      <th className="px-2 py-1 text-xs border border-gray-300">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {variants.map((v) => (
                      <tr key={v.id}>
                        <td
                          className="px-2 py-1 text-xs border border-gray-300 hover:underline cursor-pointer"
                          onClick={() => {
                            setSelectedVariant(v);
                            setModalAddVariant(true);
                          }}
                        >
                          <div>{v.display_name}</div>
                          <div>
                            {(v?.tags ?? []).map((tag, index) => (
                              <div
                                key={index}
                                className={`inline-block bg-gray-200 rounded-full px-1.5 text-[8pt] font-semibold text-gray-700 mr-2 `}
                                style={{
                                  backgroundColor: tag.color,
                                  color: invertColor(tag.color),
                                }}
                              >
                                {tag.name}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="px-2 py-1 text-xs border text-center border-gray-300">
                          {money(v.price)}
                          {v?.original_price != v?.price && (
                            <>
                              {" "}
                              <br />
                              <span className="line-through text-xs text-gray-400">
                                {money(v?.original_price)}
                              </span>
                            </>
                          )}
                        </td>
                        <td className="px-2 py-1 text-xs border border-gray-300 text-left">
                          <ul className="">
                            {v.attributes.map((a) => (
                              <li key={a.id}>
                                {a.attribute?.name} : {a.value}
                              </li>
                            ))}
                          </ul>
                        </td>
                        <td className="px-2 py-1 text-xs border border-gray-300 text-center">
                          {v.length} x {v.width} x {v.height} cm
                        </td>
                        <td className="px-2 py-1 text-xs border border-gray-300 text-center">
                          {v.weight} gr
                        </td>
                        <td className="px-2 py-1 text-xs border border-gray-300 text-center">
                          {v.sku}
                        </td>
                        <td className="px-2 py-1 text-xs border border-gray-300 text-center">
                          {money(v.total_stock)}
                        </td>

                        <td className="px-2 py-1 text-xs border border-gray-300 text-center">
                          <div className="flex justify-center">
                            <BsTrash3
                              className="text-red-500 hover:text-red-800 cursor-pointer"
                              size={12}
                              onClick={() => removeVariant(v.id)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-gray-400">No Variants Available</p>
              )}
            </div>
            <div className="w-full p-2"></div>
            <div className="mb-4">
              <div className="flex flex-row justify-between items-center">
                <small className="font-bold">Discounts:</small>
                <IconButton
                  appearance="ghost"
                  color="green"
                  size="xs"
                  className="ml-2"
                  onClick={() => {
                    setModalAddDiscount(true);
                  }}
                  icon={<PlusIcon />}
                >
                  Add Discount
                </IconButton>
              </div>
            </div>
            {(discounts ?? []).length > 0 ? (
              <table className="w-full border border-gray-300 mt-4">
                <thead>
                  <tr>
                    <th className="px-2 py-1 text-xs border text-left border-gray-300">
                      Discount Type
                    </th>
                    <th className="px-2 py-1 text-xs border border-gray-300">
                      Value
                    </th>
                    <th className="px-2 py-1 text-xs border border-gray-300">
                      Date
                    </th>
                    <th className="px-2 py-1 text-xs border border-gray-300">
                      Notes
                    </th>
                    <th className="px-2 py-1 text-xs border border-gray-300">
                      Status
                    </th>
                    <th className="px-2 py-1 text-xs border border-gray-300">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {discounts.map((v) => (
                    <tr key={v.id}>
                      <td
                        className="px-2 py-1 text-xs border border-gray-300 hover:underline cursor-pointer text-semibold"
                        onClick={() => {
                          setSelectedDiscount(v);
                          setModalAddDiscount(true);
                        }}
                      >
                        {v.type}
                      </td>
                      <td className="px-2 py-1 text-xs border text-center border-gray-300">
                        {v.type === "PERCENTAGE"
                          ? `${v.value}%`
                          : money(v.value)}
                      </td>
                      <td className="px-2 py-1 text-xs border border-gray-300 text-left">
                        <div className="flex justify-center items-center">
                          <Moment format="DD/MM/YYYY">{v.start_date}</Moment>{" "}
                          {v.end_date && " - "}
                          {v.end_date && (
                            <Moment format="DD/MM/YYYY">{v.end_date}</Moment>
                          )}{" "}
                        </div>
                      </td>
                      <td className="px-2 py-1 text-xs border text-center border-gray-300">
                        {v.notes}
                      </td>
                      <td className="px-2 py-1 text-xs border border-gray-300 text-center">
                        <div className="flex justify-center items-center">
                          {v.is_active && (
                            <BsCheck2Circle
                              size={12}
                              className="text-green-500"
                            />
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-1 text-xs border border-gray-300 text-center">
                        <div className="flex justify-center">
                          <BsTrash3
                            className="text-red-500 hover:text-red-800 cursor-pointer"
                            size={12}
                            onClick={() => {
                              if (
                                window.confirm(
                                  "Are you sure you want to delete this discount?"
                                )
                              ) {
                                // Proceed with the delete action
                                setIsLoading(true);
                                deleteDiscount(productId!, v.id!)
                                  .then(() => {
                                    successToast(
                                      "Discount deleted successfully"
                                    );
                                    getDetail();
                                  })
                                  .catch((err) => {
                                    errorToast(err.message);
                                  })
                                  .finally(() => {
                                    setIsLoading(false);
                                  });
                              }
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-gray-400">No Discounts Available</p>
            )}
            <div className="w-full p-2"></div>

            <div className="mb-4">
              <small className="font-bold">Description:</small>
              <div
                className="min-h-[100px] text-sm"
                dangerouslySetInnerHTML={{
                  __html: product?.description
                    ? nl2br(product?.description)
                    : "",
                }}
              ></div>
            </div>
            <div className="mb-4">
              <small className="font-bold">Stock Movement:</small>
              {stockMovements.length > 0 ? (
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="px-2 py-1 text-xs border text-left border-gray-300">
                        Date
                      </th>
                      <th className="px-2 py-1 text-xs border border-gray-300">
                        Quantity
                      </th>

                      <th className="px-2 py-1 text-xs border border-gray-300">
                        Merchant
                      </th>
                      <th className="px-2 py-1 text-xs border border-gray-300">
                        Notes
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockMovements.map((v) => (
                      <tr key={v.id}>
                        <td className="px-2 py-1 text-xs border border-gray-300 text-left">
                          <Moment format="DD/MM/YYYY HH:mm">{v.date}</Moment>
                        </td>
                        <td className="px-2 py-1 text-xs border border-gray-300 text-center">
                          {v.quantity}
                        </td>

                        <td
                          className="px-2 py-1 text-xs border cursor-pointer hover:underline border-gray-300 text-center"
                          onClick={() => {
                            nav(`/merchant/${v.merchant?.id}`);
                          }}
                        >
                          {v.merchant?.name}
                        </td>
                        <td className="px-2 py-1 text-xs border border-gray-300 text-center">
                          {v.description}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-gray-400">
                  No Stock Movements Available
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      {renderInputFile()}
      <ProductForm
        product={product}
        open={modalEdit}
        onClose={() => setModalEdit(false)}
        onSubmit={saveProduct}
      />
      <PriceForm
        open={modalAddPrice}
        onClose={() => setModalAddPrice(false)}
        onSubmit={(val) => {
          val.product_id = product?.id;
          console.log(val);
          addPrice(val);
        }}
      />
      {product && (
        <VariantForm
          open={modalAddVariant}
          onClose={() => setModalAddVariant(false)}
          onSubmit={(val) => {
            val.product_id = product?.id;
            addVariant(val);
          }}
          product={product!}
          variant={selectedVariant}
        />
      )}
      <DiscountForm
        open={modalAddDiscount}
        onClose={() => setModalAddDiscount(false)}
        onSubmit={(val) => {
          // console.log(val);
          createNewDiscount(val);
        }}
        discount={selectedDiscount}
      />
    </MainLayout>
  );
};
export default ProductDetailPage;
